import { inject, Injectable } from '@angular/core';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { AccessRightType, ApplicationSettingsType, ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { AppTextService } from '@fi-shared/app-text';
import { FiOnboardingCategoryType, FiOnboardingStatusType } from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { FiOnboardingCategories } from '@fi-app/fi-onboarding/fi-onboarding-consts';
import { FiOnboardingService } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding.service';
import { IOnboardingParameter } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { getObjectTypeNameTextId } from '@fi-shared/utils';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ISideListOptionsItem } from '@fi-app/shared/fi-items-list/fi-items-list';
import { FiCustomerDataService } from '@fi-app/shared/fi-shared-services/fi-customer-data.service';
import { ProgressStatusConfigType } from '@fi-app/shared/fi-progress-badge/fi-progress-badge';
import { BehaviorSubject, Observable } from 'rxjs';
import { IFiDialogOptions, IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';

@Injectable()
export class FiOnboardingHelperService {
    private readonly accessToApplicationTypeService = inject(AccessToApplicationTypeService);
    private readonly appTextService = inject(AppTextService);
    private readonly fiOnboardingService = inject(FiOnboardingService);
    private readonly fiDialogsService = inject(FiDialogsService);
    private readonly fiStateService = inject(FiStateService);
    private readonly fiCustomerDataService = inject(FiCustomerDataService);

    readonly OnboardingStatusesConfig: ProgressStatusConfigType<number> = {
        [0]: {
            textId: 'common.inactive',
            statusClass: 'fi-progress-badge--not-started',
        },
        [1]: {
            textId: 'common.active',
            statusClass: 'fi-progress-badge--completed',
        },
    };

    readonly availableCategoryTitles: Partial<Record<FiOnboardingCategoryType, string>> = {
        [FiOnboardingCategoryType.Independent]: this.getCategoryTitle(FiOnboardingCategoryType.Independent),
        [FiOnboardingCategoryType.SyncReady]: this.getCategoryTitle(FiOnboardingCategoryType.SyncReady),
    };

    protected getCategoryTitle(category: FiOnboardingCategoryType): string {
        return this.getCategoryText(category) + ' ' + this.appTextService.get('onboarding.onboarding').toLowerCase();
    }

    private getCommonOnboardingBuilderListSideListOptions(): ISideListOptionsItem[] {
        return [
            { value: SideListOptionType.SharedWithMe, text: this.appTextService.get('common.shared_with_me') },
            { value: SideListOptionType.CreatedBy, text: this.appTextService.get('common.created_by_me') },
        ];
    }

    private getCategoryOnboardingBuilderListSideListOptions(): ISideListOptionsItem[] {
        return [
            {
                value: SideListOptionType.OnboardingIndependent,
                text: this.appTextService.get('onboarding.independent'),
            },
            {
                value: SideListOptionType.OnboardingSynchronized,
                text: this.appTextService.get('onboarding.synchronized'),
            },
        ];
    }

    getOnboardingBuilderListSideListOptions(): ISideListOptionsItem[] {
        return this.fiCustomerDataService.isSynchronizedOnboardingEnabled()
            ? [
                  ...this.getCommonOnboardingBuilderListSideListOptions(),
                  ...this.getCategoryOnboardingBuilderListSideListOptions(),
              ]
            : this.getCommonOnboardingBuilderListSideListOptions();
    }

    getCategoryText(category: FiOnboardingCategoryType) {
        const textId = FiOnboardingCategories.find((item) => item.id === category)?.textId;
        if (!textId) {
            throw new Error(`getCategoryText: unknown onboarding category ${category}`);
        }
        return this.appTextService.get(textId);
    }

    getStatusText(IsActive: boolean): string {
        return this.appTextService.get(IsActive ? 'common.active' : 'common.inactive');
    }

    getItemSharedStatusLabel(isShared: boolean): string {
        return this.appTextService.get(isShared ? 'common.public' : 'task.private');
    }

    isCommonOnboarding(category: FiOnboardingCategoryType): boolean {
        return category != FiOnboardingCategoryType.Synchronized;
    }

    isDocumentStep(objectType: ObjectTypes): boolean {
        return objectType === ObjectTypes.Document;
    }

    isMediaBankFileStep(objectType: ObjectTypes): boolean {
        return objectType === ObjectTypes.MediaBank;
    }

    isTodoListStep(objectType: ObjectTypes): boolean {
        return objectType === ObjectTypes.TodoList;
    }

    isQuizStep(objectType: ObjectTypes): boolean {
        return objectType === ObjectTypes.Quiz;
    }

    //#region step elements deleting (to-do lists, quizzes)
    showStepElementDeletingConfirmationDialog(args: {
        stepElementObjectId: number;
        stepElementObjectType: ObjectTypes;
        stepElementName: string;
        deleteFn: () => void;
    }) {
        const parameter: IOnboardingParameter = {
            FetchParams: {
                FetchItems: true,
            },
            //Ids: [-1],
            StepElementObjectIds: [args.stepElementObjectId],
            StepElementObjectTypes: [args.stepElementObjectType],
        };
        this.fiOnboardingService.getLightWeightOnboardings(parameter).then((onboardings) => {
            const objectTypeName = this.appTextService
                .get(getObjectTypeNameTextId(args.stepElementObjectType))
                .toLowerCase();
            if (!onboardings.length) {
                this.fiDialogsService
                    .confirm({
                        headerText: this.appTextService.get('common.delete_item', {
                            '{0}': args.stepElementName,
                        }),
                        messageText: this.appTextService.get('onboarding.delete_step_element_confirm_message', {
                            '{object_type_name}': objectTypeName,
                        }),
                        yesButtonText: this.appTextService.get('common.delete'),
                        headerIconClass: 'fa-regular fa-trash red',
                        isBigSizeTopBorder: true,
                        colorClass: 'red',
                        config: {
                            panelClass: ['fi-rounded-dialog-panel'],
                        },
                    })
                    .onYesButtonClick(() => {
                        args.deleteFn();
                    });
            } else {
                this.fiDialogsService
                    .confirm({
                        headerText: this.appTextService.get('common.delete_item', {
                            '{0}': args.stepElementName,
                        }),
                        messageText: this.appTextService.get('onboarding.delete_step_element_in_use_message', {
                            '{cnt}': onboardings.length,
                            '{onboarding_names}': onboardings.map((onboarding) => onboarding.Name).join(', '),
                            '{object_type_name}': objectTypeName,
                        }),
                        yesButtonText: this.appTextService.get('onboarding.show_all_onboardings'),
                        headerIconClass: 'fa-regular fa-trash red',
                        isBigSizeTopBorder: true,
                        colorClass: 'red',
                        config: {
                            panelClass: ['fi-rounded-dialog-panel'],
                        },
                    })
                    .onYesButtonClick(() => {
                        this.fiStateService.go(FiStateNames.Onboarding.List, {
                            ids: onboardings.map((onboarding) => onboarding.Id),
                            stepElementObjectIds: [args.stepElementObjectId],
                            stepElementObjectTypes: [args.stepElementObjectType],
                        });
                    });
            }
        });
    }
    //#endregion

    private _allOnboardingsCount$ = new BehaviorSubject<number>(0);
    get allOnboardingsCount$(): Observable<number> {
        return this._allOnboardingsCount$.asObservable();
    }

    private _notStartedOnboardingsCount$ = new BehaviorSubject<number>(0);
    get notStartedOnboardingsCount$(): Observable<number> {
        return this._notStartedOnboardingsCount$.asObservable();
    }

    private _inProgressOnboardingsCount$ = new BehaviorSubject<number>(0);
    get inProgressOnboardingsCount$(): Observable<number> {
        return this._inProgressOnboardingsCount$.asObservable();
    }

    private _completedOnboardingsCount$ = new BehaviorSubject<number>(0);
    get completedOnboardingsCount$(): Observable<number> {
        return this._completedOnboardingsCount$.asObservable();
    }

    setOnboardingsCount() {
        this.fiOnboardingService
            .getOnboardingsCountForTabs({
                FilterType: undefined,
            })
            .then((result) => {
                result.forEach((countItem) => {
                    const status = countItem.Status;
                    if (status === FiOnboardingStatusType.NotStarted) {
                        this._notStartedOnboardingsCount$.next(countItem.OnboardingsCount);
                    } else if (status === FiOnboardingStatusType.InProgress) {
                        this._inProgressOnboardingsCount$.next(countItem.OnboardingsCount);
                    } else if (status === FiOnboardingStatusType.Completed) {
                        this._completedOnboardingsCount$.next(countItem.OnboardingsCount);
                    } else {
                        throw new Error(`Unknown status in loadAllOnboardingsCount ${status}`);
                    }
                });
                this._allOnboardingsCount$.next(result.reduce((acc, curr) => acc + curr.OnboardingsCount, 0));
            });
    }

    showElementInUseDialog(onboardingIds: number[], messageText: string, headerText: string): IFiDialogResult {
        const hasAccessToOnboardings = this.accessToApplicationTypeService.HasAccessToApplicationType(
            ApplicationSettingsType.OnboardingLocal,
            AccessRightType.Read,
        );
        const dialogData: IFiDialogOptions<any, any> = {
            messageText: messageText,
            headerText: headerText,
            noButtonText: hasAccessToOnboardings ? this.appTextService.get('onboarding.show_onboardings') : undefined,
        };
        const dialogRef = this.fiDialogsService.error(dialogData);
        dialogRef.onNoButtonClick(() => {
            this.fiStateService.go(FiStateNames.Onboarding.List, { ids: onboardingIds });
        });
        return dialogRef;
    }
}
